import React, { useState } from 'react';
import { Space, Spin, Tabs, Typography } from 'antd';

import {
    BarChartOutlined,
    BookOutlined,
    FileTextOutlined,
    FundOutlined,
    LineChartOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';

import { useManagementStore, useTradeBookStore } from '../../store';
import { TRADEBOOK } from '../../utilities/functions';

import ProfitabilityComparisonTab from './components/profitability-comparison';
import StrategyEditor from './components/strategy-editor';
import StrategySetsTab from './components/strategy-sets';
import StrategyStatistics from './components/strategy-statistics';
import TradingPlanEditor from './components/trading-plan-editor';
import TradingPlanSelection from './components/trading-plan-selection';

import './styles.scss';

const { Text } = Typography;

export default function TradeBookPage() {
    const { selectedPlan, setSelectedPlan, selectedBreakdownSection } = useTradeBookStore();
    const { publicView } = useManagementStore((state) => state);
    const isPublicReadOnly = Boolean(publicView?.isActive);

    const [activeTab, setActiveTab] = useState('statistics');

    // Fetch all tradebook data on mount
    const { isLoading: plansLoading, data: plans } = useQuery(
        ['tradebook-plans'],
        TRADEBOOK.fetchTradingPlans,
        { retry: false }
    );

    const { isFetching: breakdownFetching, data: breakdown } = useQuery(
        ['tradebook-breakdown', selectedPlan],
        () => (selectedPlan ? TRADEBOOK.fetchBreakdown(selectedPlan) : Promise.resolve(null)),
        {
            enabled: !!selectedPlan,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            staleTime: Infinity,
            retry: false
        }
    );

    const { data: global } = useQuery(['tradebook-global'], TRADEBOOK.fetchGlobal, {
        retry: false
    });

    const { isLoading: statsLoading, data: stats } = useQuery(
        ['tradebook-stats', selectedPlan],
        () =>
            selectedPlan ? TRADEBOOK.fetchStrategyStats(selectedPlan) : TRADEBOOK.fetchAllStats(),
        { retry: false }
    );

    const handleSelectPlan = (planId) => {
        setSelectedPlan(planId);
        // Auto-switch to Trading Plan tab when a plan is selected
        if (planId) {
            setActiveTab('tradingPlan');
        }
    };

    if (plansLoading || breakdownFetching) {
        return (
            <Space direction="vertical" className="no-data" wrap>
                <Spin size="large" />
                <Text>Loading TradeBook...</Text>
            </Space>
        );
    }

    // Tab content components
    const renderStatisticsTab = () => (
        <div className="tab-content-wrapper">
            <StrategyStatistics
                stats={stats}
                isLoading={statsLoading}
                selectedPlan={selectedPlan}
            />
        </div>
    );

    const renderTradingPlanTab = () => (
        <div className="tab-content-wrapper">
            {selectedPlan ? (
                <TradingPlanEditor planId={selectedPlan} />
            ) : (
                <div className="tradebook-empty-state">
                    <Typography.Title level={4}>No Trading Plan Selected</Typography.Title>
                    <Typography.Text>
                        Select or create a trading plan from the left sidebar to view details
                    </Typography.Text>
                </div>
            )}
        </div>
    );

    const renderEditorTab = () => (
        <div className="tab-content-wrapper editor-tab">
            <StrategyEditor
                selectedPlanId={selectedPlan}
                breakdownData={breakdown}
                globalData={global}
                initialSection={selectedBreakdownSection}
                isReadOnly={isPublicReadOnly}
            />
        </div>
    );

    const renderStrategySetsTab = () => (
        <div className="tab-content-wrapper">
            <StrategySetsTab />
        </div>
    );

    const renderProfitabilityTab = () => (
        <div className="tab-content-wrapper">
            <ProfitabilityComparisonTab />
        </div>
    );

    const tabItems = [
        {
            key: 'statistics',
            label: (
                <span>
                    <BarChartOutlined />
                    Statistics
                </span>
            ),
            children: renderStatisticsTab()
        },
        {
            key: 'tradingPlan',
            label: (
                <span>
                    <FileTextOutlined />
                    Trading Plan
                </span>
            ),
            children: renderTradingPlanTab()
        },
        {
            key: 'editor',
            label: (
                <span>
                    <BookOutlined />
                    TradeBook
                </span>
            ),
            children: renderEditorTab()
        },
        {
            key: 'strategySets',
            label: (
                <span>
                    <FundOutlined />
                    Strategy Sets
                </span>
            ),
            children: renderStrategySetsTab()
        },
        {
            key: 'profitability',
            label: (
                <span>
                    <LineChartOutlined />
                    Profitability
                </span>
            ),
            children: renderProfitabilityTab()
        }
    ];

    return (
        <div className={`tradebook-page ${isPublicReadOnly ? 'read-only' : ''}`}>
            <div className="tradebook-new-layout">
                {/* Left Sidebar - Trading Plan Selection (Always visible) */}
                <div className="sidebar-section">
                    <TradingPlanSelection
                        plans={plans}
                        selectedPlan={selectedPlan}
                        onSelectPlan={handleSelectPlan}
                    />
                </div>

                {/* Main Content Area with Tabs */}
                <div className="main-content-section">
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                        size="large"
                        tabPosition="bottom"
                        className="tradebook-tabs"
                    />
                </div>
            </div>
        </div>
    );
}
